/**
 * Where a district / estate suggestion NAVIGATES, derived from its per-mode
 * counts (LET-89).
 *
 * THE DEFECT THIS MAKES UNREPRESENTABLE. The dropdown used to show a mode-blind
 * "1 listing" and link every district/estate to the CURRENT browse mode's hub
 * (`/listings/rent` from the home search). An estate whose only listing was for
 * SALE therefore read "1 listing" and landed on a hub that showed none. The
 * count and the link were computed from two different predicates, and nothing
 * tied them together.
 *
 * Now the wire carries the count PER MODE, computed by the hub's own SQL
 * predicate, and this module is the ONLY way a suggestion turns into a link:
 *
 *   - a HUB destination needs a `PositiveCount`, which `positiveCount()` — the
 *     one constructor — mints only from a number > 0. So a link to
 *     `/listings/{mode}` cannot be built for a mode whose count is zero: not
 *     "should not", cannot — there is no value of the type to hand it.
 *   - the preferred mode (the surface the user is on) wins when it has
 *     listings; otherwise the OTHER mode, if it has any; otherwise the
 *     entity's own page, which always exists and never says "0 listings".
 *
 * The label the row renders is derived from the SAME destination, so it names
 * the mode the link goes to ("1 for sale"), never a total no hub shows.
 */
import type { ModeCounts, SuggestionItemVariants } from '@/shared/lib/types/suggestions';
import type { ListingsMode, Route } from '@letsgethome/routes';

declare const positiveCountBrand: unique symbol;

/**
 * A listing count known to be > 0. The brand has exactly one minter,
 * {@link positiveCount}; a hub destination cannot be constructed without one,
 * which is what keeps a zero-count hub link out of the type system rather
 * than out of a code review.
 */
type PositiveCount = number & { readonly [positiveCountBrand]: true };

/** The one minter: `undefined` for anything that is not a positive integer. */
export function positiveCount(n: number): PositiveCount | undefined {
  return Number.isInteger(n) && n > 0 ? (n as PositiveCount) : undefined;
}

/**
 * Where a suggestion goes. `hub` carries the mode it links to AND the count
 * that justified it — the row's label reads both, so the label and the link
 * cannot name different modes.
 */
export type SuggestionDestination =
  | {
      readonly kind: 'hub';
      readonly mode: ListingsMode;
      readonly count: PositiveCount;
      readonly href: Route;
    }
  | { readonly kind: 'entity'; readonly href: Route };

/** The two things a caller must know how to build; the derivation owns the choice. */
type DestinationHrefs = {
  /** The listings hub for `mode`, filtered to this suggestion's entity. */
  readonly hub: (mode: ListingsMode) => Route;
  /** The entity's own page — the fall-through when neither hub has listings. */
  readonly entity: Route;
};

/** A suggestion that navigates to a hub — the variants carrying `counts`. */
export type HubSuggestion = SuggestionItemVariants['district'] | SuggestionItemVariants['estate'];

/** The other browse mode. Total over the two-member union. */
function otherMode(mode: ListingsMode): ListingsMode {
  return mode === 'rent' ? 'buy' : 'rent';
}

/** The count a mode's hub would show. Total over `ListingsMode`. */
function countFor(counts: ModeCounts, mode: ListingsMode): number {
  return mode === 'rent' ? counts.rent : counts.sale;
}

/**
 * Pick the hub mode for `counts`, preferring `preferred`: the preferred mode
 * if it has listings, else the other mode if IT has listings, else none. The
 * returned count is the proof that the chosen hub is non-empty.
 */
export function hubModeFor(
  counts: ModeCounts,
  preferred: ListingsMode
): { mode: ListingsMode; count: PositiveCount } | undefined {
  for (const mode of [preferred, otherMode(preferred)]) {
    const count = positiveCount(countFor(counts, mode));
    if (count !== undefined) return { mode, count };
  }
  return undefined;
}

/**
 * The single derivation every consumer goes through — the href builders in
 * `SuggestionItem.tsx` and the row's count label alike.
 */
export function suggestionDestination(
  item: HubSuggestion,
  preferred: ListingsMode,
  hrefs: DestinationHrefs
): SuggestionDestination {
  const hub = hubModeFor(item.counts, preferred);
  if (hub === undefined) return { kind: 'entity', href: hrefs.entity };
  return { kind: 'hub', mode: hub.mode, count: hub.count, href: hrefs.hub(hub.mode) };
}
